import type { MachineAttachmentDto, MachineDto } from '@api/machines'
import { useTranslation } from 'react-i18next'

import {
  useFetchAttachmentsByBrandName,
  useFetchMachinesByBrandName
} from '@/shared-queries/machines'
import { StyledGrid } from './StyledGrid'

export interface Equipments {
  attachments: MachineAttachmentDto[]
  machines: MachineDto[]
}

export const SelectEquipmentsStep = ({
  brand,
  isMachine,
  itemId,
  selectedEquipments,
  setSelectedEquipments,
  translationKeyPrefix
}: {
  brand: null | string | undefined
  isMachine: boolean
  itemId: number | undefined
  selectedEquipments: Equipments
  setSelectedEquipments: React.Dispatch<React.SetStateAction<Equipments>>
  translationKeyPrefix: string
}) => {
  const { data: machinesData = [] } = useFetchMachinesByBrandName({
    brandName: brand ?? ''
  })
  const { data: attachmentsData = [] } = useFetchAttachmentsByBrandName({
    brandName: brand ?? ''
  })

  const machines = machinesData?.filter(machine => machine?.id !== itemId) ?? []
  const attachments =
    attachmentsData?.filter(attachment => attachment?.id !== itemId) ?? []

  const { t } = useTranslation('', {
    keyPrefix: translationKeyPrefix
  })

  return (
    <StyledGrid
      columnsProps={[
        {
          caption: t('model') ?? '',
          dataField: 'modelName'
        },
        {
          caption: t('type') ?? '',
          dataField: 'category'
        },
        {
          caption: t('manufactureYear') ?? '',
          dataField: 'manufactureYear'
        },
        {
          caption: t('stockType') ?? '',
          dataField: 'stockType'
        }
      ]}
      headerFilterProps={{
        visible: true
      }}
      onCellPrepared={e =>
        e.rowType === 'data' &&
        e.cellElement.style.setProperty('cursor', 'pointer', 'important')
      }
      onContentReady={e => {
        e.component.selectRows(
          isMachine
            ? selectedEquipments.machines
            : selectedEquipments.attachments,
          true
        )
      }}
      onRowClick={e => {
        const grid = e.component
        const rowKey = grid.getKeyByRowIndex(e.rowIndex)
        if (e.isSelected) {
          grid.deselectRows([rowKey])
        } else {
          grid.selectRows([rowKey], true)
        }
      }}
      onSelectionChanged={selectedRows => {
        const { selectedRowsData } = selectedRows

        if (isMachine) {
          setSelectedEquipments(prevState => ({
            ...prevState,
            machines: selectedRowsData
          }))
        } else {
          setSelectedEquipments(prevState => ({
            ...prevState,
            attachments: selectedRowsData
          }))
        }
      }}
      searchPanelProps={{
        highlightSearchText: true,
        placeholder: t('searchPlaceholder') ?? '',
        visible: true
      }}
      selection={{
        mode: 'multiple',
        selectAllMode: 'allPages',
        showCheckBoxesMode: 'always'
      }}
      dataSource={isMachine ? machines : attachments}
    />
  )
}
